import { Controller } from '@hotwired/stimulus';
import { getForm } from '../utilities/form';

export default class extends Controller {
    static targets = ['preview', 'highlights', 'actionButton', 'statusLine', 'textEdited', 'resetButton', 'chosen'];
    static values = {
        template: String,
        periodLabel: String,
        childName: String,
        activityName: String,
        noteKey: String,
        conjunction: { type: String, default: 'ir' },
    };

    connect() {
        // show what the server will save under the current config (regenerate() keeps hand-edited text)
        this.regenerate();

        // re-measure the highlight layer when the textarea is resized or the modal finishes showing
        if (this.hasPreviewTarget && this.hasHighlightsTarget && window.ResizeObserver) {
            this._previewObserver = new ResizeObserver(() => this._syncHighlightBox());
            this._previewObserver.observe(this.previewTarget);
        }
    }

    disconnect() {
        this._previewObserver?.disconnect();
    }

    /**
     * Rebuild the preview from the selections. Skipped once the coach edited the text by hand,
     * so selection changes never wipe manual edits (resetText() rebuilds explicitly).
     */
    regenerate() {
        this._renderChosen();

        if (!this.hasPreviewTarget || this._isTextEdited()) {
            this.updateHighlights();
            return;
        }

        this._rebuild();
    }

    resetText() {
        this._setTextEdited(false);
        this._rebuild();
    }

    _rebuild() {
        if (!this.hasPreviewTarget) {
            return;
        }

        const form = this.element.querySelector('#gr-form');

        if (!form) {
            return;
        }

        const values = {};

        form.querySelectorAll('select[name^="answers["]').forEach((select) => {
            const selected = this._selectedValues(select);

            if (selected.length) {
                values[this._fieldKey(select.name)] = select.multiple ? this._joinList(selected) : selected[0];
            }
        });

        const note = form.querySelector(`textarea[name="answers[${this.noteKeyValue}]"]`);
        const noteText = note ? note.value.trim() : '';

        // reserved values win over same-named section keys, as in GrHelper::buildText()
        values.laikotarpis = this.periodLabelValue;
        values.vardas = this.childNameValue;
        values.veikla = this.activityNameValue;
        // as typed, closed with a period when it has no end punctuation — as in GrHelper::buildText()
        values[this.noteKeyValue] = noteText === '' || /[.!?…]$/u.test(noteText) ? noteText : `${noteText}.`;

        this.previewTarget.value = this._render(this.templateValue, values);
        this.updateHighlights();
    }

    handlePreviewInput() {
        this._setTextEdited(true);
        this.updateHighlights();
    }

    _isTextEdited() {
        return this.hasTextEditedTarget && this.textEditedTarget.value === '1';
    }

    _setTextEdited(edited) {
        if (this.hasTextEditedTarget) {
            this.textEditedTarget.value = edited ? '1' : '0';
        }

        if (this.hasResetButtonTarget) {
            this.resetButtonTarget.hidden = !edited;
        }
    }

    syncHighlightScroll() {
        if (this.hasHighlightsTarget) {
            this.highlightsTarget.scrollTop = this.previewTarget.scrollTop;
        }
    }

    updateHighlights() {
        if (!this.hasHighlightsTarget || !this.hasPreviewTarget) {
            return;
        }

        const form = this.element.querySelector('#gr-form');
        const text = this.previewTarget.value;

        this._syncHighlightBox();

        if (!form || text.trim() === '') {
            this.highlightsTarget.innerHTML = this._escape(text);
            return;
        }

        const selected = [];

        form.querySelectorAll('select[name^="answers["]').forEach((select) => {
            selected.push(...this._selectedValues(select).map((value) => value.trim()));
        });

        // the coach note is inserted as typed (end punctuation aside, see _rebuild())
        const note = form.querySelector(`textarea[name="answers[${this.noteKeyValue}]"]`);

        if (note) {
            selected.push(note.value.trim().replace(/\.+$/, ''));
        }

        let html = this._escape(text);

        const unique = selected
            .filter((value, index, self) => value !== '' && self.indexOf(value) === index)
            .sort((a, b) => b.length - a.length);

        if (unique.length) {
            const pattern = unique.map((value) => this._escapeRegExp(this._escape(value))).join('|');
            // case-insensitive: a value opening a sentence is capitalized in the text
            html = html.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
        }

        this.highlightsTarget.innerHTML = html;
        this.syncHighlightScroll();
    }

    /**
     * The highlight layer must wrap text exactly like the textarea above it: copy the textarea's
     * computed text/box metrics (they come from the .input theme styles) and leave room for its scrollbar.
     */
    _syncHighlightBox() {
        const style = window.getComputedStyle(this.previewTarget);
        const layer = this.highlightsTarget.style;

        [
            'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'wordSpacing',
            'textTransform', 'textIndent', 'tabSize', 'boxSizing',
            'paddingTop', 'paddingLeft', 'paddingBottom',
            'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
        ].forEach((property) => {
            layer[property] = style[property];
        });

        const scrollbar = this.previewTarget.offsetWidth - this.previewTarget.clientWidth
            - parseFloat(style.borderLeftWidth) - parseFloat(style.borderRightWidth);

        layer.paddingRight = `${parseFloat(style.paddingRight) + Math.max(0, scrollbar)}px`;

        // not laid out yet (modal still hidden): keep the CSS inset size until the observer fires
        if (this.previewTarget.offsetWidth > 0) {
            layer.width = `${this.previewTarget.offsetWidth}px`;
            layer.height = `${this.previewTarget.offsetHeight}px`;
        }
    }

    async submit(event) {
        event.preventDefault();

        const button = event.currentTarget;
        const formEl = this.element.querySelector('#gr-form');

        if (!formEl || button.disabled) {
            return;
        }

        const form = getForm(formEl);

        if (event.params.url) {
            form.setUrl(event.params.url);
        }

        button.classList.add('is-loading');
        button.disabled = true;

        try {
            await form.submit(event, {}, button);
        } catch (e) {
            // validation/request errors are rendered by the form error event
        } finally {
            button.classList.remove('is-loading');
            button.disabled = Boolean(this._locked);
        }
    }

    stopLoading() {
        this.actionButtonTargets.forEach((button) => {
            button.classList.remove('is-loading');
        });
    }

    handleSuccess(event) {
        this.stopLoading();

        const data = event.detail.response.data || {};

        // a status in the response means the record was written (confirm prompts carry none)
        if (data.status) {
            this.markChanged();
        }

        if (data.message) {
            this._showStatus(data.message, data.status);
        }

        if (data.status === 'sent') {
            this._lock();
        }
    }

    /**
     * Delivered GRs are final — freeze the form so nothing suggests it can still be changed
     */
    _lock() {
        this._locked = true;

        this.element.querySelectorAll('#gr-form input, #gr-form select, #gr-form textarea').forEach((field) => {
            field.disabled = true;
            field.tomselect?.disable();
        });

        this.actionButtonTargets.forEach((button) => {
            button.disabled = true;
        });

        if (this.hasResetButtonTarget) {
            this.resetButtonTarget.hidden = true;
        }
    }

    /**
     * Status banner under the actions: tone follows the GR status, time shows when it was saved,
     * and a short pulse makes repeated saves noticeable
     */
    _showStatus(message, tone, withTime = true) {
        if (!this.hasStatusLineTarget) {
            return;
        }

        const line = this.statusLineTarget;
        const time = new Date().toLocaleTimeString('lt-LT', { hour: '2-digit', minute: '2-digit' });

        line.textContent = withTime ? `${message} · ${time}` : message;
        line.className = `gr-status-line${tone ? ` gr-status-line--${tone}` : ''}`;

        // restart the pulse animation
        void line.offsetWidth;
        line.classList.add('is-flash');
    }

    markChanged() {
        this._changed = true;
    }

    /**
     * The leaders list behind the modal shows statuses rendered on page load —
     * refresh it once the modal closes, but only if something was actually saved
     */
    handleClose() {
        if (this._changed) {
            window.location.reload();
        }
    }

    /**
     * Mirror of GrHelper::joinList(): "a", "a ir b", "a, b ir c"
     */
    _joinList(items) {
        const values = items.map((item) => String(item).trim()).filter((item) => item !== '');

        if (values.length < 2) {
            return values[0] ?? '';
        }

        const last = values.pop();

        return `${values.join(', ')} ${this.conjunctionValue} ${last}`;
    }

    /**
     * Non-empty selected values of a (multi) select, in option order — same order the server keeps
     */
    _selectedValues(select) {
        return Array.from(select.selectedOptions)
            .map((option) => option.value)
            .filter((value) => value.trim() !== '');
    }

    /**
     * Multi-selects collapse to "N pasirinkta" — list the chosen values under each of them
     */
    _renderChosen() {
        this.chosenTargets.forEach((list) => {
            const select = this.element.querySelector(`select[name="answers[${list.dataset.key}][]"]`);

            list.replaceChildren(...(select ? this._selectedValues(select) : []).map((value) => {
                const item = document.createElement('li');
                item.textContent = value;

                return item;
            }));
        });
    }

    _fieldKey(name) {
        const match = name.match(/^answers\[([^\]]+)\]/);

        return match ? match[1] : name;
    }

    /**
     * Mirror of GrFeedback::renderTemplate(): single pass, normalized keys, unknown placeholders vanish
     */
    _render(template, values) {
        const normalized = {};

        Object.keys(values).forEach((key) => {
            normalized[this._normalizeKey(key)] = values[key];
        });

        // a value that starts a sentence in the template gets a capital first letter
        let text = template.replace(/\[([^\[\]\s]+)\]/gu, (match, key, offset) => {
            const value = normalized[this._normalizeKey(key)] ?? '';
            const before = template.slice(0, offset).trimEnd();

            if (value !== '' && (before === '' || /[.!?]$/.test(before))) {
                return value.charAt(0).toUpperCase() + value.slice(1);
            }

            return value;
        });

        text = text.replace(/\s+/g, ' ');
        text = text.replace(/ ([.,!?])/g, '$1');

        return text.trim();
    }

    /**
     * Mirror of GrHelper::normalizeSectionKey()
     */
    _normalizeKey(key) {
        return String(key)
            .trim()
            .replace(/^\[+|\]+$/g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9_]+/g, '_')
            .replace(/^_+|_+$/g, '');
    }

    _escape(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    _escapeRegExp(value) {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
